import users from '@/api/users'
import employee from '@/api/employee'
import warehouse from '@/api/warehouse'
import role from '@/api/role'
import branch from '@/api/branch'
import subProgram from '@/api/sub-program'
import Permission from '@/api/permission'
import {
  transform,
  groupBy,
  uniq,
  map,
  head,
  capitalize,
  sortBy,
  lowerCase
} from 'lodash'

import { baseState, baseMutations } from '@/store/mixins'

const getPermissionOrder = suffix => {
  switch (lowerCase(suffix)) {
    case 'view':
      return 0

    case 'create':
      return 1

    case 'update':
      return 2

    case 'delete':
      return 3

    default:
      return 4
  }
}

export const state = {
  ...baseState,
  employees: [],
  warehouses: [],
  permissions: [],
  searchPermissionText: '',
  user_type: 'staff-api',
  branch_id: null,
  disable: 0
}

export const getters = {
  permissionGroups: state => {
    const permissions = state.permissions.filter(p => {
      return (
        p.resource
          .toLowerCase()
          .includes(state.searchPermissionText.toLowerCase()) ||
        (p.group !== null &&
          p.group
            .toLowerCase()
            .includes(state.searchPermissionText.toLowerCase()))
      )
    })

    return transform(
      groupBy(permissions, permission => capitalize(permission.group)),
      (result, value, key) => {
        result[key] = groupBy(
          sortBy(value, p => getPermissionOrder(head(p.name.split('-')))),
          permission => capitalize(permission.resource)
        )
      },
      {}
    )
  },
  permissionColumns: state => {
    return sortBy(
      uniq(
        map(state.permissions, p => {
          return capitalize(head(p.name.split('-')))
        })
      ),
      p => getPermissionOrder(p)
    )
  }
}

export const mutations = {
  ...baseMutations,
  RECEIVE_PERMISSIONS(state, permissions) {
    state.permissions = permissions.map(permission => {
      return {
        id: permission.id,
        name: permission.name,
        resource: permission.label,
        group: permission.group
      }
    })
  },
  SET_SEARCH_PERMISSION(state, searchPermissionText) {
    state.searchPermissionText = searchPermissionText
  },
  SET_EMPLOYEE_LIST(state, employee) {
    state.employees = employee.map(el => ({
      value: el.staff_id,
      label: el.name_en
    }))
  },
  SET_WAREHOUSE_LIST(state, data) {
    state.warehouses = data
  },
  SET_USER_TYPE(state, data) {
    state.user_type = data
  },
  SET_BRANCH_ID(state, data) {
    state.branch_id = data
  },
  SET_DISABLED (state, data) {
    state.disable = data
  }
}

export const actions = {
  fetch(context, attrubutes = {}) {
    let params = Object.assign(
      {
        page: context.state.pagination.page,
        perPage: context.state.pagination.perPage,
        search: context.state.search,
        user_type: context.state.user_type,
        disable: context.state.disable,
        all_branch: Array.isArray(context.state.branch_id)
          ? context.state.branch_id.length
            ? undefined
            : true
          : true
      },
      attrubutes
    )
    return users.get({ params }).then(response => {
      context.commit('RECEIVE_RESOURCES', response)
      return Promise.resolve(response)
    })
  },
  store(context, data) {
    return users.store(data)
  },
  update(context, { id, ...data }) {
    return users.update(id, data)
  },
  async find(context, id) {
    if (_.isEmpty(context.state.edit_data)) {
      let response = await users.find(id)
      context.commit('SET_EDIT_DATA', response.data)
    }
  },
  resetpwd(context, { id, data }) {
    return users.resetPassword(
      id,
      Object.assign(data, { user_type: context.state.user_type })
    )
  },
  switchDefaultBranch(context, data) {
    return users.switchDefaultBranch(data)
  },
  async getDataBeforCreate(context) {
    if (!context.state.employees.length > 0) {
      let response = await employee.get({
        params: {
          fields: { setup_staff_information: 'staff_id,name_en' },
          page: -1
        }
      })
      context.commit('SET_EMPLOYEE_LIST', response.data)
    }
  },
  getRole(context) {
    return role.get({
      params: {
        fields: { roles: 'id,name' },
        filter: {
          guard_name: 'staff-api'
        },
        page: -1
      }
    })
  },
  getBranch(context, attrubutes = {}) {
    let params = Object.assign(
      {
        include: 'company',
        fields: {
          setup_branch_profiles: 'branch_id,branch_name_en,company_id',
          company: 'company_id,company_name_en,system_url'
        },
        page: -1
      },
      attrubutes
    )
    return branch.get({ params })
  },
  getSubProgram(context) {
    return subProgram.get({
      params: {
        fields: {
          academic_program_subs: 'acps_id,acps_name_foreign'
        },
        page: -1
      }
    })
  },
  fetchPermissions(context, attributes) {
    if (context.state.permissions.length > 0) return

    return Permission.get({
      params: Object.assign({ page: -1 }, attributes)
    }).then(response => {
      context.commit('RECEIVE_PERMISSIONS', response.data)
      return Promise.resolve(response)
    })
  },
  getWarehouse(context, attributes = {}) {
    return warehouse
      .get({
        params: {
          fields: {
            setup_inventory_locations:
              'location_id,location_name,is_main_warehouse'
          },
          page: -1,
          ...attributes
        },
        headers: {
          'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
        }
      })
      .then(response => {
        context.commit('SET_WAREHOUSE_LIST', response.data)
        return Promise.resolve(response)
      })
  },
  unlock(context, data) {
    return users.unlock(data)
  },
  changeUserType(context, data = {}) {
    return users.changeUserType(data)
  },
  changeDisable(context, { id, data }) {
    return users.changeDisable(
      id,
      Object.assign(data, { user_type: context.state.user_type })
    )
  },
  changeAllAboutMyDay(context, { id, data }) {
    return users.changeAllAboutMyDay(id, Object.assign(data))
  },
  getCurrentUser(context, attributes = {}) {
    return users.currentUser({ params: attributes });
  },
}
